(function () {
  const SEL = {
    root: '[data-cart-drawer], .cart-drawer, #cart-drawer, .drawer',
    panel: '.cart-drawer__panel, .drawer__inner',
    overlay: '[data-cart-drawer-overlay], #CartDrawer-Overlay, .cart-drawer__overlay',
    close: '[data-cart-drawer-close], .cart-drawer__close, [aria-label="Close cart"], [data-close-cart]',
    items: '[data-cart-items], #CartDrawer-CartItems, .cart-drawer__items, .drawer__contents',
    subtotal: '[data-cart-subtotal], .totals__total-value, #CartDrawer-Subtotal',
    addButtons: '[data-quick-buy], .productitem--action-atc, [aria-label="Add to cart"], [data-product-atc], .product-form--atc-button, .add-to-cart-wrap .add-to-cart',
    qtyPlus: '[data-qty-plus], .quantity__button[name="plus"]',
    qtyMinus: '[data-qty-minus], .quantity__button[name="minus"]',
    qtyInput: '[data-qty], .quantity__input',
    remove: '[data-remove], .cart-remove-button',
    lineWrap: '[data-line], .cart-item',
  };
  // Added visually hidden to make it hidden by default to avoid interuption of the FCP
  const CLASS = { open: 'is-open', visuallyHidden: 'visually-hidden' };
  let isProcessingATC = false;
  let addToCartCallCount = 0;
  let lastAddToCartTime = 0;

  const ATC_DEBUG = true; // Set to false to reduce console noise once done debugging

  function atcLog() {
    if (ATC_DEBUG && console && console.log) console.log.apply(console, ['[ATC-DEBUG]'].concat(Array.from(arguments)));
  }

  // Intercept fetch to prevent duplicate cart adds (especially from Extend + Subscriptions)
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];

    if (typeof url === 'string' && url.includes('/cart/add')) {
      const now = Date.now();
      const timeSinceLastAdd = now - lastAddToCartTime;

      if (isProcessingATC && timeSinceLastAdd < 500) {
        addToCartCallCount++;
        atcLog('FETCH INTERCEPTED (duplicate blocked). isProcessingATC=', isProcessingATC, 'timeSinceLastAdd=', timeSinceLastAdd);
        return Promise.resolve({
          ok: true,
          status: 200,
          json: () => Promise.resolve({ blocked: true, message: 'Duplicate call blocked' })
        });
      }

      lastAddToCartTime = now;
      addToCartCallCount = 0;
      atcLog('FETCH /cart/add.js allowed (will run)');
    }

    return originalFetch.apply(this, args);
  };

  let els = {};
  function syncEls() {
    els.root = document.querySelector(SEL.root);
    els.panel = els.root ? els.root.querySelector(SEL.panel) : null;
    els.overlay = els.root ? els.root.querySelector(SEL.overlay) : null;
    els.items = els.root ? els.root.querySelector(SEL.items) : null;
    els.subtotals = els.root ? els.root.querySelectorAll(SEL.subtotal) : [];
  }
  syncEls();

  let prevFocused = null, focusables = [], firstFocusable = null, lastFocusable = null;
  const FOCUSABLE_SELECTOR = [
    'a[href]','area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  function isOpen() {
    return !!(els.root && els.root.getAttribute('aria-hidden') !== 'true');
  }

  let atcOpenDrawerFallbackTimer = null;
  const ATC_OPEN_DRAWER_FALLBACK_MS = 1500;

  function clearAtcOpenDrawerFallback() {
    if (atcOpenDrawerFallbackTimer != null) {
      clearTimeout(atcOpenDrawerFallbackTimer);
      atcOpenDrawerFallbackTimer = null;
    }
  }

  function scheduleAtcOpenDrawerFallback() {
    clearAtcOpenDrawerFallback();
    atcOpenDrawerFallbackTimer = setTimeout(() => {
      atcOpenDrawerFallbackTimer = null;
      syncEls();
      if (!isOpen()) {
        openDrawer();
        try {
          fetchAndRender().catch(() => {});
        } catch (_) {}
      }
    }, ATC_OPEN_DRAWER_FALLBACK_MS);
  }

  function isInPanel(node) {
    return !!(els.panel && node && els.panel.contains(node));
  }

  async function getFirstAvailableVariantIdByHandle(handle) {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Product JSON not found for handle: ' + handle);
    const prod = await res.json();
    const v = (prod.variants || []).find(v => v.available) || (prod.variants || [])[0];
    if (!v?.id) throw new Error('No variants found for handle: ' + handle);
    return v.id;
  }

  async function addVariantToCart(variantId, qty = 1) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: Number(qty) }),
      credentials: 'same-origin'
    });
    if (!r.ok) {
      const data = await r.json();
      throw new Error(data.description || data.message || 'ATC failed');
    }
    return r.json();
  }

  function computeFocusables() {
    if (!els.panel) return;
    focusables = Array.from(els.panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(el => el.offsetParent !== null || el === els.panel);
    firstFocusable = focusables[0] || els.panel;
    lastFocusable  = focusables[focusables.length - 1] || els.panel;
  }
  function trapFocus() {
    prevFocused = document.activeElement;
    if (els.panel) {
      els.panel.setAttribute('role', 'dialog');
      els.panel.setAttribute('aria-modal', 'true');
      if (!els.panel.hasAttribute('tabindex')) els.panel.setAttribute('tabindex', '-1');
    }
    computeFocusables();
    setTimeout(() => (firstFocusable || els.panel || els.root).focus({ preventScroll: true }), 0);
  }
  function releaseFocus() {
    if (prevFocused && typeof prevFocused.focus === 'function') {
      prevFocused.focus({ preventScroll: true });
    }
  }

  let scrollPosition = 0;

  function lockScroll() {
    // Save current scroll position
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Lock scroll without affecting layout or sticky elements
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.style.overscrollBehavior = 'contain';
  }

  function unlockScroll() {
    // First restore the scroll position while body is still fixed
    const currentScrollPosition = scrollPosition;

    // Remove fixed positioning
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overscrollBehavior = '';

    // Restore scroll position instantly (no animation)
    if (currentScrollPosition > 0) {
      window.scrollTo({ top: currentScrollPosition, behavior: 'instant' });
    }
  }

  function openDrawer() {
    syncEls();
    if (!els.root) return;
    lockScroll();
    els.root.setAttribute('aria-hidden', 'false');
    els.root.classList.remove(CLASS.visuallyHidden);
    els.root.classList.add(CLASS.open);
    trapFocus();
  }
  function closeDrawer() {
    syncEls();
    if (!els.root) return;
    els.root.setAttribute('aria-hidden', 'true');
    els.root.classList.remove(CLASS.open);
    unlockScroll();
    releaseFocus();
  }

  function fmt(cents){
    try {
    if (window.Shopify?.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    } catch(_){}
    const mf = String(window.theme?.moneyFormat || '$ {{amount}}');
    const symbol = mf.replace(/\s*\{\{.*\}\}\s*/g, '').trim() || '$';
    return symbol + (cents/100).toFixed(2);
  }

  function getBaseImgUrl(it) {
    let u = it?.image || it?.featured_image?.url || it?.featured_image || '';
    if (!u) return '';
    if (u.startsWith('//')) u = 'https:' + u;
    return u;
  }
  function withParam(u, key, val) {
    return u + (u.includes('?') ? '&' : '?') + encodeURIComponent(key) + '=' + encodeURIComponent(val);
  }
  function toCents(v){
    if (v == null) return 0;
    if (typeof v === 'number') return Math.round(v);
    if (typeof v === 'string') return Math.round(parseFloat(v.replace(/[^0-9.]/g,'')) * 100) || 0;
    return 0;
  }

  function deriveLinePricing(it, variantCompareCents = 0) {
    const qty = Number(it.quantity || 1);

    const unitFinal    = Number((it.final_price ?? it.price) ?? 0);
    const unitOriginal = Number((it.original_price ?? it.price) ?? 0);
    const unitCompare  = variantCompareCents > 0 ? variantCompareCents : Number((it.compare_at_price ?? it.variant_compare_at_price) ?? 0);

    const lineFinal    = Number((it.final_line_price ?? it.line_price) ?? (unitFinal * qty)) || 0;
    const lineOriginal = Number((it.original_line_price ?? (unitOriginal * qty))) || 0;

    let lineCompare = 0;
    if (lineOriginal > lineFinal) {
      lineCompare = lineOriginal;
    } else if (unitCompare > unitFinal) {
      lineCompare = unitCompare * qty;
    }

    return { lineFinal, lineCompare };
  }

  function blockCartRedirect(ms = 1500) {
    const until = Date.now() + ms;
    
    const shouldBlock = (url) => {
      const urlStr = String(url);
      return Date.now() < until && 
             /\/cart(?:$|[/?#])/i.test(urlStr) && 
             !/\/checkout/i.test(urlStr); 
    };
    
    const guard = (fn) => function(url){ 
      if (shouldBlock(url)) {
        console.log('[Cart Debug] Blocked redirect to:', url);
        return; 
      }
      return fn.apply(this, arguments); 
    };
    
    if (!blockCartRedirect._patched) {
      blockCartRedirect._patched = true;
      const loc = window.location;
      try {
        loc.assign  = guard(loc.assign.bind(loc));
        loc.replace = guard(loc.replace.bind(loc));
      } catch (e) {
        // location.assign/replace are read-only in many browsers (CSP, secure context).
        // Skip patching so we don't throw; drawer still opens and __blockCartRedirectUntil still set.
      }
      try {
        const origPush = history.pushState.bind(history);
        const origRep  = history.replaceState.bind(history);
        history.pushState   = function(){ 
          if (!shouldBlock(arguments[2]||'')) return origPush.apply(this, arguments); 
          console.log('[Cart Debug] Blocked pushState to:', arguments[2]);
        };
        history.replaceState= function(){ 
          if (!shouldBlock(arguments[2]||'')) return origRep.apply(this, arguments); 
          console.log('[Cart Debug] Blocked replaceState to:', arguments[2]);
        };
      } catch (e) {
        // history may be read-only in some contexts
      }
    }
    window.__blockCartRedirectUntil = until;
  }

  function updateHeaderCartUI(cart) {
    const count = Number(cart?.item_count || 0);
    const nodes = document.querySelectorAll('.site-header-cart--count, [data-header-cart-count]');
    nodes.forEach((el) => {
      el.setAttribute('data-header-cart-count', count > 0 ? String(count) : '');
      el.classList.toggle('visible', count > 0);
      el.textContent = '';
      el.setAttribute('aria-hidden', 'true');
    });
    const live = document.getElementById('cart-live-region');
    if (live) live.textContent = count === 1 ? '1 item in cart' : `${count} items in cart`;
    const btn = document.querySelector('.site-header-cart--button');
    if (btn) {
      const labelCount = count === 0 ? 'empty' : (count === 1 ? '1 item' : `${count} items`);
      btn.setAttribute('aria-label', `Cart, ${labelCount}`);
    }
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials:'same-origin' });
    if (!r.ok) throw new Error('cart.js failed');
    return r.json();
  }
  async function updateLine(line, quantity){
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {'Content-Type':'application/json','Accept':'application/json'},
      body: JSON.stringify({ line:Number(line), quantity:Number(quantity) }),
      credentials:'same-origin'
    });
    if (!r.ok) return;
    const cart = await r.json();
    await renderCart(cart);
  }

  const variantCompareCache = new Map();
  async function getVariantCompareCents(variantId) {
    const vid = Number(variantId);
    if (!vid) return 0;
    if (variantCompareCache.has(vid)) return variantCompareCache.get(vid);
    try {
      const r = await fetch(`/variants/${vid}.js`, { credentials:'same-origin' });
      if (!r.ok) throw 0;
      const v = await r.json();
      const cents = toCents(v?.compare_at_price);
      variantCompareCache.set(vid, cents || 0);
      return cents || 0;
    } catch {
      variantCompareCache.set(vid, 0);
      return 0;
    }
  }

  async function renderCart(cart){
    syncEls();
    if (!els.items) return;

    els.items.innerHTML = '';
    const pending = [];

    if (!cart.items?.length){
      const emptyText = window.cartEmptyText || 'Your cart is empty.';
      els.items.classList.add('is-empty');
      els.items.innerHTML = `
        <div class="cart-empty-state">
          <p class="cart-empty-state__text">${emptyText}</p>
          <a href="/collections/for-sale"
             class="button-primary mdc-ripple-surface mdc-ripple-upgraded"
             data-continue-shopping
             aria-label="Continue shopping, go to For Sale collection">
            Continue shopping
          </a>
        </div>
      `;
      if (els.subtotal) els.subtotal.textContent = fmt(0);
    } else {
      els.items.classList.remove('is-empty');
      const tpl = document.getElementById('cart-item-template');
      cart.items.forEach((it, idx) => {
        let node;
        if (tpl?.content?.firstElementChild) {
          node = tpl.content.firstElementChild.cloneNode(true);
        } else {
          node = document.createElement('article');
          node.className = 'cart-item';
          node.innerHTML = `
            <a class="cart-item__image" data-href tabindex="-1" aria-hidden="true">
              <img data-image alt="">
            </a>
            <div class="cart-item__info">
              <a data-title class="cart-item__title"></a>
              <div class="cart-item__controls">
                <div class="qty">
                  <button data-qty-minus type="button">−</button>
                  <input data-qty type="number" min="0" step="1">
                  <button data-qty-plus type="button">+</button>
                </div>
                <div class="cart-item__prices">
                  <span data-line-price class="money"></span>
                  <del data-line-compare class="money" hidden></del>
                </div>
                <button data-remove type="button">Remove</button>
              </div>
            </div>`;
        }

        node.dataset.line = idx + 1;

        const aImg   = node.querySelector('[data-href]');
        const img    = node.querySelector('img[data-image]');
        const title  = node.querySelector('[data-title]');
        const variant= node.querySelector('[data-variant]');
        const variantOptions = node.querySelector('[data-variant-options]');
        const props  = node.querySelector('[data-props]');
        const qtyInp = node.querySelector(SEL.qtyInput);
        const price  = node.querySelector('[data-line-price]');
        const compare= node.querySelector('[data-line-compare]');

        const productUrl = it.url || (it.handle ? `/products/${it.handle}?variant=${it.variant_id}` : '#');
        if (aImg) aImg.href = productUrl;
        if (title) {
          title.href = productUrl;
          const cleanedTitle = String(it.product_title || it.title || '').replace(/^\s*lectron[\s-]*/i, '');
          title.textContent = cleanedTitle;
        }

        // Display variant options (Color, Choose Tesla Model, etc.)
        if (variantOptions && it.options_with_values && it.options_with_values.length > 0) {
          const hasNonDefaultOptions = it.options_with_values.some(opt =>
            opt.value && opt.value !== 'Default Title' && opt.name !== 'Title'
          );

          if (hasNonDefaultOptions) {
            const optionsHTML = it.options_with_values
              .filter(opt => opt.value && opt.value !== 'Default Title' && opt.name !== 'Title')
              .map(opt => `
                <div class="cart-item--product-options">
                  <span class="cart-item--option-name">${opt.name}</span>
                  ${opt.value}
                </div>
              `)
              .join('');
            variantOptions.innerHTML = optionsHTML;
          }
        }

        const raw = getBaseImgUrl(it);
        if (img && raw) {
          const w300 = withParam(raw, 'width', 300);
          const w600 = withParam(raw, 'width', 600);
          img.loading = 'lazy';
          img.decoding = 'async';
          img.src = w300;
          img.srcset = `${w300} 1x, ${w600} 2x`;
          img.alt = it.product_title || it.title || '';
          img.onerror = () => { img.removeAttribute('srcset'); img.src = raw; };
        } else if (img) {
          img.removeAttribute('srcset'); img.removeAttribute('src'); img.alt = 'No image';
        }

        if (variant && it.variant_title && it.variant_title !== 'Default Title') variant.textContent = it.variant_title;
        if (props && it.properties) {
          const entries = Object.entries(it.properties).filter(([k,v]) => v);
          if (entries.length) props.innerHTML = entries.map(([k,v]) => `<div><small>${k}: ${v}</small></div>`).join('');
        }

        if (qtyInp) qtyInp.value = it.quantity;

        let { lineFinal, lineCompare } = deriveLinePricing(it, 0);
        if (price) {
          price.textContent = fmt(lineFinal);
          price.classList.toggle('cart-item__price--discount', lineCompare > lineFinal);
        }
        if (compare) {
          if (lineCompare > lineFinal) {
            compare.textContent = fmt(lineCompare);
            compare.hidden = false;
          } else {
            compare.textContent = '';
            compare.hidden = true;
          }
        }

        if ((!lineCompare || lineCompare <= lineFinal) && it.variant_id) {
          const work = (async () => {
            const vcmp = await getVariantCompareCents(it.variant_id);
            if (vcmp > 0) {
              const derived = deriveLinePricing(it, vcmp);
              if (derived.lineCompare > derived.lineFinal && compare) {
                compare.textContent = fmt(derived.lineCompare);
                compare.hidden = false;
                price && price.classList.add('cart-item__price--discount');
              }
            }
          })();
          pending.push(work);
        }

        els.items.appendChild(node);
      });
    }

    if (pending.length) {
      try { await Promise.all(pending); } catch {}
    }

    (els.subtotals || []).forEach(n => n.textContent = fmt(cart.total_price));
    document.querySelectorAll('[data-checkout-total]').forEach(n => {
      n.textContent = fmt(cart.total_price);
    });
    updateHeaderCartUI(cart);
    computeFocusables();
    updateCheckoutButtons(cart);
  }

  async function fetchAndRender() {
    const cart = await fetchCart();
    await renderCart(cart);
  }

  function updateCheckoutButtons(cart) {
    if (isUpdatingButtons) return;
    isUpdatingButtons = true;
    
    try {
      const checkoutBtns = document.querySelectorAll(
        '[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout, input[name="checkout"], form[action*="/checkout"] button[type="submit"]'
      );
      
      const hasItems = cart && cart.items && cart.items.length > 0;
      lastKnownCart = cart;
      
      checkoutBtns.forEach((btn) => {
        if (!hasItems) {
          btn.disabled = true;
          btn.setAttribute('disabled', 'true');
          btn.classList.add('is-disabled');
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.disabled = false;
          btn.removeAttribute('disabled');
          btn.classList.remove('is-disabled');
          btn.removeAttribute('aria-disabled');
        }
      });
    } finally {
      setTimeout(() => { isUpdatingButtons = false; }, 50);
    }
  }

  window.fetchAndRender = fetchAndRender;
  window.openDrawer = openDrawer;
  window.updateCheckoutButtons = updateCheckoutButtons;

  let lastKnownCart = null;
  let isUpdatingButtons = false;
  
  const buttonObserver = new MutationObserver((mutations) => {
    if (!lastKnownCart || isUpdatingButtons) return;
    
    let shouldUpdate = false;
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
        const target = mutation.target;
        if (target.matches && target.matches('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout')) {
          const hasItems = lastKnownCart.items && lastKnownCart.items.length > 0;
          const isDisabled = target.disabled || target.hasAttribute('disabled');
          if ((hasItems && isDisabled) || (!hasItems && !isDisabled)) {
            shouldUpdate = true;
            break;
          }
        }
      } else if (mutation.type === 'childList') {
        const added = Array.from(mutation.addedNodes);
        const hasCheckoutButton = added.some(node => 
          node.nodeType === 1 && (
            node.matches && node.matches('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout') ||
            node.querySelector && node.querySelector('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout')
          )
        );
        if (hasCheckoutButton) {
          shouldUpdate = true;
          break;
        }
      }
    }
    
    if (shouldUpdate) {
      updateCheckoutButtons(lastKnownCart);
    }
  });

  buttonObserver.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-disabled']
  });

  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden) {
      try {
        const cart = await fetchCart();
        await renderCart(cart);
        updateCheckoutButtons(cart);
      } catch (err) {
        console.warn('Error refreshing cart on visibility change:', err);
      }
    }
  });

  window.addEventListener('pageshow', async (event) => {
    try {
      const cart = await fetchCart();
      await renderCart(cart);
      updateCheckoutButtons(cart);
    } catch (err) {
      console.warn('Error refreshing cart on pageshow:', err);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
      try {
        const cart = await fetchCart();
        updateCheckoutButtons(cart);
      } catch (err) {
        console.warn('Error updating checkout buttons on DOMContentLoaded:', err);
      }
    });
  } else {
    (async () => {
      try {
        const cart = await fetchCart();
        updateCheckoutButtons(cart);
      } catch (err) {
        console.warn('Error updating checkout buttons on init:', err);
      }
    })();
  }

  setInterval(async () => {
    if (!document.hidden && !isOpen()) {
      try {
        //const cart = await fetchCart();
        //updateCheckoutButtons(cart);
      } catch (err) {}
    }
  }, 3000);

  window.addEventListener('refreshAjaxSideCart', async () => {
    try {
      blockCartRedirect(1500);
      if (typeof openDrawer === 'function') openDrawer();
      if (typeof fetchAndRender === 'function') await fetchAndRender();
    } catch (err) {
      console.warn('Error refreshing cart after Extend event', err);
    }
  });

  // ERROR BANNER FUNCTION
  function showErrorBanner(message) {
    if (typeof MessageBanner !== 'undefined') {
      new MessageBanner(message, 'error');
      return;
    }

    const bannerTemplate = document.querySelector('[data-templates] [data-message-banner]');
    if (!bannerTemplate) {
      console.error('Error:', message);
      alert(message);
      return;
    }

    const banner = bannerTemplate.cloneNode(true);
    const messageElement = banner.querySelector('[data-message-banner-content]');
    messageElement.innerHTML = message;
    banner.classList.add('message--error');
    banner.setAttribute('data-animation-state', 'open');

    const modal = document.querySelector('.modal-loaded .modal-inner');
    const target = modal || document.querySelector('[data-site-header]');
    target.appendChild(banner);

    setTimeout(() => banner.remove(), 5000);

    const closeButton = banner.querySelector('[data-message-banner-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => banner.remove());
    }
  }

  // SUBMIT LISTENER
  document.addEventListener('submit', async (ev) => {
    const form = ev.target;
    if (!form || !form.matches('form[action*="/cart/add"]')) return;

    // NEXUS CRO hero owns ATC (CCS addon + Extend).
    if (
      window.__nexusCroAtcHandled ||
      form.closest('nexus-cro-hero') ||
      form.closest('[data-nc-purchase]') ||
      (ev.submitter && ev.submitter.closest('[data-nc-hero-atc], .nc-hero__atc, nexus-cro-hero'))
    ) {
      console.log('[CART-DRAWER] NEXUS CRO hero submit — skipping');
      return;
    }

    // Vortex hero owns ATC when the Extend offer is on the page.
    if (
      window.__vpAtcHandled ||
      (document.querySelector('[data-vp-extend-offer]') && form.closest('[data-vp-hero], [data-vp-sticky]'))
    ) {
      console.log('[CART-DRAWER] Vortex hero submit — skipping');
      return;
    }

    // Check if this submit was triggered from sticky bar button
    const submitter = ev.submitter;
    if (submitter) {
      const isFromStickyBar = submitter.closest('.sticky-bar, .sticky-bar-form');
      if (isFromStickyBar) {
        console.log('[CART-DRAWER] Submit from sticky bar detected, letting addtocart-sticky.js handle it');
        return; // Let addtocart-sticky.js handle it
      }
    }

    // Check if addtocart-sticky.js already handled this
    if (window.__stickyAtcHandled) {
      console.log('[CART-DRAWER] Sticky already handled flag detected');
      return;
    }

    console.log('[CART-DRAWER] SUBMIT handler processing');

    if (isProcessingATC) {
      ev.preventDefault();
      return;
    }

    const extendInThisForm = !!(form.querySelector('.extend-offer'));
    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    const subsTypeRadio = document.querySelector('input[name^="subs_type_"]:checked');
    const isSubscriptionSelected = subsTypeRadio && subsTypeRadio.value !== 'one_time';

    let sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';
    if (isSubscriptionSelected && !sellingPlanValue) {
      const intervalSelect = document.querySelector('select[name="subs_interval"]');
      if (intervalSelect && sellingPlanInput) {
        sellingPlanValue = intervalSelect.value;
        sellingPlanInput.value = sellingPlanValue;
      }
    }

    const hasSubscription = isSubscriptionSelected && sellingPlanValue && sellingPlanValue !== '';

    if (extendInThisForm && ev.isTrusted && !hasSubscription) {
      return;
    }

    if (extendInThisForm && hasSubscription) {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      window.__blockingExtendForSubscription = true;
      setTimeout(() => delete window.__blockingExtendForSubscription, 2000);
    }

    if (!ev.defaultPrevented) ev.preventDefault();
    if (ev.stopPropagation) ev.stopPropagation();
    if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
    isProcessingATC = true;
    scheduleAtcOpenDrawerFallback();
    atcLog('PATH: FORM SUBMIT handler');

    let addToCartSucceeded = false;
    try {
      const fd = new FormData(form);
      atcLog('Form submit: posting to /cart/add.js');
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: fd,
        credentials: 'same-origin'
      });

      const rawText = await r.text();
      atcLog('Form submit response: status=', r.status, 'body length=', rawText.length);
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        atcLog('Form submit: response is not JSON. First 200 chars:', rawText.slice(0, 200));
        throw parseErr;
      }

      if (!r.ok) {
        const errorMessage = data.description || data.message || 'Unable to add item to cart';
        atcLog('Form submit: API error', errorMessage, data);
        showErrorBanner(errorMessage);
        return;
      }
      if (data.blocked) atcLog('Form submit: response had data.blocked=', data.blocked);

      addToCartSucceeded = true;
      blockCartRedirect(1500);
      clearAtcOpenDrawerFallback();
      openDrawer();

      try {
        await fetchAndRender();
      } catch (renderErr) {
        console.warn('fetchAndRender error (non-critical):', renderErr);
      }

      try {
        document.dispatchEvent(new CustomEvent('cart:added'));
      } catch (eventErr) {
        console.warn('cart:added event error (non-critical):', eventErr);
      }
    } catch (e) {
      atcLog('[ATC-ERROR-1] Form submit handler error:', e?.message || e, e);
      console.warn('[ATC-ERROR-1] Form submit handler error', e);
      if (!addToCartSucceeded) {
        showErrorBanner('An error occurred while adding to cart');(function () {
  const SEL = {
    root: '[data-cart-drawer], .cart-drawer, #cart-drawer, .drawer',
    panel: '.cart-drawer__panel, .drawer__inner',
    overlay: '[data-cart-drawer-overlay], #CartDrawer-Overlay, .cart-drawer__overlay',
    close: '[data-cart-drawer-close], .cart-drawer__close, [aria-label="Close cart"], [data-close-cart]',
    items: '[data-cart-items], #CartDrawer-CartItems, .cart-drawer__items, .drawer__contents',
    subtotal: '[data-cart-subtotal], .totals__total-value, #CartDrawer-Subtotal',
    addButtons: '[data-quick-buy], .productitem--action-atc, [aria-label="Add to cart"], [data-product-atc], .product-form--atc-button, .add-to-cart-wrap .add-to-cart',
    qtyPlus: '[data-qty-plus], .quantity__button[name="plus"]',
    qtyMinus: '[data-qty-minus], .quantity__button[name="minus"]',
    qtyInput: '[data-qty], .quantity__input',
    remove: '[data-remove], .cart-remove-button',
    lineWrap: '[data-line], .cart-item',
  };
  // Added visually hidden to make it hidden by default to avoid interuption of the FCP
  const CLASS = { open: 'is-open', visuallyHidden: 'visually-hidden' };
  let isProcessingATC = false;
  let addToCartCallCount = 0;
  let lastAddToCartTime = 0;

  const ATC_DEBUG = true; // Set to false to reduce console noise once done debugging

  function atcLog() {
    if (ATC_DEBUG && console && console.log) console.log.apply(console, ['[ATC-DEBUG]'].concat(Array.from(arguments)));
  }

  // Intercept fetch to prevent duplicate cart adds (especially from Extend + Subscriptions)
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    const url = args[0];

    if (typeof url === 'string' && url.includes('/cart/add')) {
      const now = Date.now();
      const timeSinceLastAdd = now - lastAddToCartTime;

      if (isProcessingATC && timeSinceLastAdd < 500) {
        addToCartCallCount++;
        atcLog('FETCH INTERCEPTED (duplicate blocked). isProcessingATC=', isProcessingATC, 'timeSinceLastAdd=', timeSinceLastAdd);
        return Promise.resolve({
          ok: true,
          status: 200,
          json: () => Promise.resolve({ blocked: true, message: 'Duplicate call blocked' })
        });
      }

      lastAddToCartTime = now;
      addToCartCallCount = 0;
      atcLog('FETCH /cart/add.js allowed (will run)');
    }

    return originalFetch.apply(this, args);
  };

  let els = {};
  function syncEls() {
    els.root = document.querySelector(SEL.root);
    els.panel = els.root ? els.root.querySelector(SEL.panel) : null;
    els.overlay = els.root ? els.root.querySelector(SEL.overlay) : null;
    els.items = els.root ? els.root.querySelector(SEL.items) : null;
    els.subtotals = els.root ? els.root.querySelectorAll(SEL.subtotal) : [];
  }
  syncEls();

  let prevFocused = null, focusables = [], firstFocusable = null, lastFocusable = null;
  const FOCUSABLE_SELECTOR = [
    'a[href]','area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
  ].join(',');

  function isOpen() {
    return !!(els.root && els.root.getAttribute('aria-hidden') !== 'true');
  }

  let atcOpenDrawerFallbackTimer = null;
  const ATC_OPEN_DRAWER_FALLBACK_MS = 1500;

  function clearAtcOpenDrawerFallback() {
    if (atcOpenDrawerFallbackTimer != null) {
      clearTimeout(atcOpenDrawerFallbackTimer);
      atcOpenDrawerFallbackTimer = null;
    }
  }

  function scheduleAtcOpenDrawerFallback() {
    clearAtcOpenDrawerFallback();
    atcOpenDrawerFallbackTimer = setTimeout(() => {
      atcOpenDrawerFallbackTimer = null;
      syncEls();
      if (!isOpen()) {
        openDrawer();
        try {
          fetchAndRender().catch(() => {});
        } catch (_) {}
      }
    }, ATC_OPEN_DRAWER_FALLBACK_MS);
  }

  function isInPanel(node) {
    return !!(els.panel && node && els.panel.contains(node));
  }

  async function getFirstAvailableVariantIdByHandle(handle) {
    const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Product JSON not found for handle: ' + handle);
    const prod = await res.json();
    const v = (prod.variants || []).find(v => v.available) || (prod.variants || [])[0];
    if (!v?.id) throw new Error('No variants found for handle: ' + handle);
    return v.id;
  }

  async function addVariantToCart(variantId, qty = 1) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(variantId), quantity: Number(qty) }),
      credentials: 'same-origin'
    });
    if (!r.ok) {
      const data = await r.json();
      throw new Error(data.description || data.message || 'ATC failed');
    }
    return r.json();
  }

  function computeFocusables() {
    if (!els.panel) return;
    focusables = Array.from(els.panel.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(el => el.offsetParent !== null || el === els.panel);
    firstFocusable = focusables[0] || els.panel;
    lastFocusable  = focusables[focusables.length - 1] || els.panel;
  }
  function trapFocus() {
    prevFocused = document.activeElement;
    if (els.panel) {
      els.panel.setAttribute('role', 'dialog');
      els.panel.setAttribute('aria-modal', 'true');
      if (!els.panel.hasAttribute('tabindex')) els.panel.setAttribute('tabindex', '-1');
    }
    computeFocusables();
    setTimeout(() => (firstFocusable || els.panel || els.root).focus({ preventScroll: true }), 0);
  }
  function releaseFocus() {
    if (prevFocused && typeof prevFocused.focus === 'function') {
      prevFocused.focus({ preventScroll: true });
    }
  }

  let scrollPosition = 0;

  function lockScroll() {
    // Save current scroll position
    scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Lock scroll without affecting layout or sticky elements
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = '100%';
    document.body.style.overscrollBehavior = 'contain';
  }

  function unlockScroll() {
    // First restore the scroll position while body is still fixed
    const currentScrollPosition = scrollPosition;

    // Remove fixed positioning
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.overscrollBehavior = '';

    // Restore scroll position instantly (no animation)
    if (currentScrollPosition > 0) {
      window.scrollTo({ top: currentScrollPosition, behavior: 'instant' });
    }
  }

  function openDrawer() {
    syncEls();
    if (!els.root) return;
    lockScroll();
    els.root.setAttribute('aria-hidden', 'false');
    els.root.classList.remove(CLASS.visuallyHidden);
    els.root.classList.add(CLASS.open);
    trapFocus();
  }
  function closeDrawer() {
    syncEls();
    if (!els.root) return;
    els.root.setAttribute('aria-hidden', 'true');
    els.root.classList.remove(CLASS.open);
    unlockScroll();
    releaseFocus();
  }

  function fmt(cents){
    try {
    if (window.Shopify?.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    } catch(_){}
    const mf = String(window.theme?.moneyFormat || '$ {{amount}}');
    const symbol = mf.replace(/\s*\{\{.*\}\}\s*/g, '').trim() || '$';
    return symbol + (cents/100).toFixed(2);
  }

  function getBaseImgUrl(it) {
    let u = it?.image || it?.featured_image?.url || it?.featured_image || '';
    if (!u) return '';
    if (u.startsWith('//')) u = 'https:' + u;
    return u;
  }
  function withParam(u, key, val) {
    return u + (u.includes('?') ? '&' : '?') + encodeURIComponent(key) + '=' + encodeURIComponent(val);
  }
  function toCents(v){
    if (v == null) return 0;
    if (typeof v === 'number') return Math.round(v);
    if (typeof v === 'string') return Math.round(parseFloat(v.replace(/[^0-9.]/g,'')) * 100) || 0;
    return 0;
  }

  function deriveLinePricing(it, variantCompareCents = 0) {
    const qty = Number(it.quantity || 1);

    const unitFinal    = Number((it.final_price ?? it.price) ?? 0);
    const unitOriginal = Number((it.original_price ?? it.price) ?? 0);
    const unitCompare  = variantCompareCents > 0 ? variantCompareCents : Number((it.compare_at_price ?? it.variant_compare_at_price) ?? 0);

    const lineFinal    = Number((it.final_line_price ?? it.line_price) ?? (unitFinal * qty)) || 0;
    const lineOriginal = Number((it.original_line_price ?? (unitOriginal * qty))) || 0;

    let lineCompare = 0;
    if (lineOriginal > lineFinal) {
      lineCompare = lineOriginal;
    } else if (unitCompare > unitFinal) {
      lineCompare = unitCompare * qty;
    }

    return { lineFinal, lineCompare };
  }

  function blockCartRedirect(ms = 1500) {
    const until = Date.now() + ms;
    
    const shouldBlock = (url) => {
      const urlStr = String(url);
      return Date.now() < until && 
             /\/cart(?:$|[/?#])/i.test(urlStr) && 
             !/\/checkout/i.test(urlStr); 
    };
    
    const guard = (fn) => function(url){ 
      if (shouldBlock(url)) {
        console.log('[Cart Debug] Blocked redirect to:', url);
        return; 
      }
      return fn.apply(this, arguments); 
    };
    
    if (!blockCartRedirect._patched) {
      blockCartRedirect._patched = true;
      const loc = window.location;
      try {
        loc.assign  = guard(loc.assign.bind(loc));
        loc.replace = guard(loc.replace.bind(loc));
      } catch (e) {
        // location.assign/replace are read-only in many browsers (CSP, secure context).
        // Skip patching so we don't throw; drawer still opens and __blockCartRedirectUntil still set.
      }
      try {
        const origPush = history.pushState.bind(history);
        const origRep  = history.replaceState.bind(history);
        history.pushState   = function(){ 
          if (!shouldBlock(arguments[2]||'')) return origPush.apply(this, arguments); 
          console.log('[Cart Debug] Blocked pushState to:', arguments[2]);
        };
        history.replaceState= function(){ 
          if (!shouldBlock(arguments[2]||'')) return origRep.apply(this, arguments); 
          console.log('[Cart Debug] Blocked replaceState to:', arguments[2]);
        };
      } catch (e) {
        // history may be read-only in some contexts
      }
    }
    window.__blockCartRedirectUntil = until;
  }

  function updateHeaderCartUI(cart) {
    const count = Number(cart?.item_count || 0);
    const nodes = document.querySelectorAll('.site-header-cart--count, [data-header-cart-count]');
    nodes.forEach((el) => {
      el.setAttribute('data-header-cart-count', count > 0 ? String(count) : '');
      el.classList.toggle('visible', count > 0);
      el.textContent = '';
      el.setAttribute('aria-hidden', 'true');
    });
    const live = document.getElementById('cart-live-region');
    if (live) live.textContent = count === 1 ? '1 item in cart' : `${count} items in cart`;
    const btn = document.querySelector('.site-header-cart--button');
    if (btn) {
      const labelCount = count === 0 ? 'empty' : (count === 1 ? '1 item' : `${count} items`);
      btn.setAttribute('aria-label', `Cart, ${labelCount}`);
    }
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials:'same-origin' });
    if (!r.ok) throw new Error('cart.js failed');
    return r.json();
  }
  async function updateLine(line, quantity){
    const r = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {'Content-Type':'application/json','Accept':'application/json'},
      body: JSON.stringify({ line:Number(line), quantity:Number(quantity) }),
      credentials:'same-origin'
    });
    if (!r.ok) return;
    const cart = await r.json();
    await renderCart(cart);
  }

  const variantCompareCache = new Map();
  async function getVariantCompareCents(variantId) {
    const vid = Number(variantId);
    if (!vid) return 0;
    if (variantCompareCache.has(vid)) return variantCompareCache.get(vid);
    try {
      const r = await fetch(`/variants/${vid}.js`, { credentials:'same-origin' });
      if (!r.ok) throw 0;
      const v = await r.json();
      const cents = toCents(v?.compare_at_price);
      variantCompareCache.set(vid, cents || 0);
      return cents || 0;
    } catch {
      variantCompareCache.set(vid, 0);
      return 0;
    }
  }

  async function renderCart(cart){
    syncEls();
    if (!els.items) return;

    els.items.innerHTML = '';
    const pending = [];

    if (!cart.items?.length){
      const emptyText = window.cartEmptyText || 'Your cart is empty.';
      els.items.classList.add('is-empty');
      els.items.innerHTML = `
        <div class="cart-empty-state">
          <p class="cart-empty-state__text">${emptyText}</p>
          <a href="/collections/for-sale"
             class="button-primary mdc-ripple-surface mdc-ripple-upgraded"
             data-continue-shopping
             aria-label="Continue shopping, go to For Sale collection">
            Continue shopping
          </a>
        </div>
      `;
      if (els.subtotal) els.subtotal.textContent = fmt(0);
    } else {
      els.items.classList.remove('is-empty');
      const tpl = document.getElementById('cart-item-template');
      cart.items.forEach((it, idx) => {
        let node;
        if (tpl?.content?.firstElementChild) {
          node = tpl.content.firstElementChild.cloneNode(true);
        } else {
          node = document.createElement('article');
          node.className = 'cart-item';
          node.innerHTML = `
            <a class="cart-item__image" data-href tabindex="-1" aria-hidden="true">
              <img data-image alt="">
            </a>
            <div class="cart-item__info">
              <a data-title class="cart-item__title"></a>
              <div class="cart-item__controls">
                <div class="qty">
                  <button data-qty-minus type="button">−</button>
                  <input data-qty type="number" min="0" step="1">
                  <button data-qty-plus type="button">+</button>
                </div>
                <div class="cart-item__prices">
                  <span data-line-price class="money"></span>
                  <del data-line-compare class="money" hidden></del>
                </div>
                <button data-remove type="button">Remove</button>
              </div>
            </div>`;
        }

        node.dataset.line = idx + 1;

        const aImg   = node.querySelector('[data-href]');
        const img    = node.querySelector('img[data-image]');
        const title  = node.querySelector('[data-title]');
        const variant= node.querySelector('[data-variant]');
        const variantOptions = node.querySelector('[data-variant-options]');
        const props  = node.querySelector('[data-props]');
        const qtyInp = node.querySelector(SEL.qtyInput);
        const price  = node.querySelector('[data-line-price]');
        const compare= node.querySelector('[data-line-compare]');

        const productUrl = it.url || (it.handle ? `/products/${it.handle}?variant=${it.variant_id}` : '#');
        if (aImg) aImg.href = productUrl;
        if (title) {
          title.href = productUrl;
          const cleanedTitle = String(it.product_title || it.title || '').replace(/^\s*lectron[\s-]*/i, '');
          title.textContent = cleanedTitle;
        }

        // Display variant options (Color, Choose Tesla Model, etc.)
        if (variantOptions && it.options_with_values && it.options_with_values.length > 0) {
          const hasNonDefaultOptions = it.options_with_values.some(opt =>
            opt.value && opt.value !== 'Default Title' && opt.name !== 'Title'
          );

          if (hasNonDefaultOptions) {
            const optionsHTML = it.options_with_values
              .filter(opt => opt.value && opt.value !== 'Default Title' && opt.name !== 'Title')
              .map(opt => `
                <div class="cart-item--product-options">
                  <span class="cart-item--option-name">${opt.name}</span>
                  ${opt.value}
                </div>
              `)
              .join('');
            variantOptions.innerHTML = optionsHTML;
          }
        }

        const raw = getBaseImgUrl(it);
        if (img && raw) {
          const w300 = withParam(raw, 'width', 300);
          const w600 = withParam(raw, 'width', 600);
          img.loading = 'lazy';
          img.decoding = 'async';
          img.src = w300;
          img.srcset = `${w300} 1x, ${w600} 2x`;
          img.alt = it.product_title || it.title || '';
          img.onerror = () => { img.removeAttribute('srcset'); img.src = raw; };
        } else if (img) {
          img.removeAttribute('srcset'); img.removeAttribute('src'); img.alt = 'No image';
        }

        if (variant && it.variant_title && it.variant_title !== 'Default Title') variant.textContent = it.variant_title;
        if (props && it.properties) {
          const entries = Object.entries(it.properties).filter(([k,v]) => v);
          if (entries.length) props.innerHTML = entries.map(([k,v]) => `<div><small>${k}: ${v}</small></div>`).join('');
        }

        if (qtyInp) qtyInp.value = it.quantity;

        let { lineFinal, lineCompare } = deriveLinePricing(it, 0);
        if (price) {
          price.textContent = fmt(lineFinal);
          price.classList.toggle('cart-item__price--discount', lineCompare > lineFinal);
        }
        if (compare) {
          if (lineCompare > lineFinal) {
            compare.textContent = fmt(lineCompare);
            compare.hidden = false;
          } else {
            compare.textContent = '';
            compare.hidden = true;
          }
        }

        if ((!lineCompare || lineCompare <= lineFinal) && it.variant_id) {
          const work = (async () => {
            const vcmp = await getVariantCompareCents(it.variant_id);
            if (vcmp > 0) {
              const derived = deriveLinePricing(it, vcmp);
              if (derived.lineCompare > derived.lineFinal && compare) {
                compare.textContent = fmt(derived.lineCompare);
                compare.hidden = false;
                price && price.classList.add('cart-item__price--discount');
              }
            }
          })();
          pending.push(work);
        }

        els.items.appendChild(node);
      });
    }

    if (pending.length) {
      try { await Promise.all(pending); } catch {}
    }

    (els.subtotals || []).forEach(n => n.textContent = fmt(cart.total_price));
    document.querySelectorAll('[data-checkout-total]').forEach(n => {
      n.textContent = fmt(cart.total_price);
    });
    updateHeaderCartUI(cart);
    computeFocusables();
    updateCheckoutButtons(cart);
  }

  async function fetchAndRender() {
    const cart = await fetchCart();
    await renderCart(cart);
  }

  function updateCheckoutButtons(cart) {
    if (isUpdatingButtons) return;
    isUpdatingButtons = true;
    
    try {
      const checkoutBtns = document.querySelectorAll(
        '[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout, input[name="checkout"], form[action*="/checkout"] button[type="submit"]'
      );
      
      const hasItems = cart && cart.items && cart.items.length > 0;
      lastKnownCart = cart;
      
      checkoutBtns.forEach((btn) => {
        if (!hasItems) {
          btn.disabled = true;
          btn.setAttribute('disabled', 'true');
          btn.classList.add('is-disabled');
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.disabled = false;
          btn.removeAttribute('disabled');
          btn.classList.remove('is-disabled');
          btn.removeAttribute('aria-disabled');
        }
      });
    } finally {
      setTimeout(() => { isUpdatingButtons = false; }, 50);
    }
  }

  window.fetchAndRender = fetchAndRender;
  window.openDrawer = openDrawer;
  window.updateCheckoutButtons = updateCheckoutButtons;

  let lastKnownCart = null;
  let isUpdatingButtons = false;
  
  const buttonObserver = new MutationObserver((mutations) => {
    if (!lastKnownCart || isUpdatingButtons) return;
    
    let shouldUpdate = false;
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
        const target = mutation.target;
        if (target.matches && target.matches('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout')) {
          const hasItems = lastKnownCart.items && lastKnownCart.items.length > 0;
          const isDisabled = target.disabled || target.hasAttribute('disabled');
          if ((hasItems && isDisabled) || (!hasItems && !isDisabled)) {
            shouldUpdate = true;
            break;
          }
        }
      } else if (mutation.type === 'childList') {
        const added = Array.from(mutation.addedNodes);
        const hasCheckoutButton = added.some(node => 
          node.nodeType === 1 && (
            node.matches && node.matches('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout') ||
            node.querySelector && node.querySelector('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout')
          )
        );
        if (hasCheckoutButton) {
          shouldUpdate = true;
          break;
        }
      }
    }
    
    if (shouldUpdate) {
      updateCheckoutButtons(lastKnownCart);
    }
  });

  buttonObserver.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['disabled', 'aria-disabled']
  });

  document.addEventListener('visibilitychange', async () => {
    if (!document.hidden) {
      try {
        const cart = await fetchCart();
        await renderCart(cart);
        updateCheckoutButtons(cart);
      } catch (err) {
        console.warn('Error refreshing cart on visibility change:', err);
      }
    }
  });

  window.addEventListener('pageshow', async (event) => {
    try {
      const cart = await fetchCart();
      await renderCart(cart);
      updateCheckoutButtons(cart);
    } catch (err) {
      console.warn('Error refreshing cart on pageshow:', err);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', async () => {
      try {
        const cart = await fetchCart();
        updateCheckoutButtons(cart);
      } catch (err) {
        console.warn('Error updating checkout buttons on DOMContentLoaded:', err);
      }
    });
  } else {
    (async () => {
      try {
        const cart = await fetchCart();
        updateCheckoutButtons(cart);
      } catch (err) {
        console.warn('Error updating checkout buttons on init:', err);
      }
    })();
  }

  setInterval(async () => {
    if (!document.hidden && !isOpen()) {
      try {
        //const cart = await fetchCart();
        //updateCheckoutButtons(cart);
      } catch (err) {}
    }
  }, 3000);

  window.addEventListener('refreshAjaxSideCart', async () => {
    try {
      blockCartRedirect(1500);
      if (typeof openDrawer === 'function') openDrawer();
      if (typeof fetchAndRender === 'function') await fetchAndRender();
    } catch (err) {
      console.warn('Error refreshing cart after Extend event', err);
    }
  });

  // ERROR BANNER FUNCTION
  function showErrorBanner(message) {
    if (typeof MessageBanner !== 'undefined') {
      new MessageBanner(message, 'error');
      return;
    }

    const bannerTemplate = document.querySelector('[data-templates] [data-message-banner]');
    if (!bannerTemplate) {
      console.error('Error:', message);
      alert(message);
      return;
    }

    const banner = bannerTemplate.cloneNode(true);
    const messageElement = banner.querySelector('[data-message-banner-content]');
    messageElement.innerHTML = message;
    banner.classList.add('message--error');
    banner.setAttribute('data-animation-state', 'open');

    const modal = document.querySelector('.modal-loaded .modal-inner');
    const target = modal || document.querySelector('[data-site-header]');
    target.appendChild(banner);

    setTimeout(() => banner.remove(), 5000);

    const closeButton = banner.querySelector('[data-message-banner-close]');
    if (closeButton) {
      closeButton.addEventListener('click', () => banner.remove());
    }
  }

  // SUBMIT LISTENER
  document.addEventListener('submit', async (ev) => {
    const form = ev.target;
    if (!form || !form.matches('form[action*="/cart/add"]')) return;

    // NEXUS CRO hero owns ATC (CCS addon + Extend).
    if (
      window.__nexusCroAtcHandled ||
      form.closest('nexus-cro-hero') ||
      form.closest('[data-nc-purchase]') ||
      (ev.submitter && ev.submitter.closest('[data-nc-hero-atc], .nc-hero__atc, nexus-cro-hero'))
    ) {
      console.log('[CART-DRAWER] NEXUS CRO hero submit — skipping');
      return;
    }

    // Vortex hero owns ATC when the Extend offer is on the page.
    if (
      window.__vpAtcHandled ||
      (document.querySelector('[data-vp-extend-offer]') && form.closest('[data-vp-hero], [data-vp-sticky]'))
    ) {
      console.log('[CART-DRAWER] Vortex hero submit — skipping');
      return;
    }

    // Check if this submit was triggered from sticky bar button
    const submitter = ev.submitter;
    if (submitter) {
      const isFromStickyBar = submitter.closest('.sticky-bar, .sticky-bar-form');
      if (isFromStickyBar) {
        console.log('[CART-DRAWER] Submit from sticky bar detected, letting addtocart-sticky.js handle it');
        return; // Let addtocart-sticky.js handle it
      }
    }

    // Check if addtocart-sticky.js already handled this
    if (window.__stickyAtcHandled) {
      console.log('[CART-DRAWER] Sticky already handled flag detected');
      return;
    }

    console.log('[CART-DRAWER] SUBMIT handler processing');

    if (isProcessingATC) {
      ev.preventDefault();
      return;
    }

    const extendInThisForm = !!(form.querySelector('.extend-offer'));
    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    const subsTypeRadio = document.querySelector('input[name^="subs_type_"]:checked');
    const isSubscriptionSelected = subsTypeRadio && subsTypeRadio.value !== 'one_time';

    let sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';
    if (isSubscriptionSelected && !sellingPlanValue) {
      const intervalSelect = document.querySelector('select[name="subs_interval"]');
      if (intervalSelect && sellingPlanInput) {
        sellingPlanValue = intervalSelect.value;
        sellingPlanInput.value = sellingPlanValue;
      }
    }

    const hasSubscription = isSubscriptionSelected && sellingPlanValue && sellingPlanValue !== '';

    if (extendInThisForm && ev.isTrusted && !hasSubscription) {
      return;
    }

    if (extendInThisForm && hasSubscription) {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      window.__blockingExtendForSubscription = true;
      setTimeout(() => delete window.__blockingExtendForSubscription, 2000);
    }

    if (!ev.defaultPrevented) ev.preventDefault();
    if (ev.stopPropagation) ev.stopPropagation();
    if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
    isProcessingATC = true;
    scheduleAtcOpenDrawerFallback();
    atcLog('PATH: FORM SUBMIT handler');

    let addToCartSucceeded = false;
    try {
      const fd = new FormData(form);
      atcLog('Form submit: posting to /cart/add.js');
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: fd,
        credentials: 'same-origin'
      });

      const rawText = await r.text();
      atcLog('Form submit response: status=', r.status, 'body length=', rawText.length);
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        atcLog('Form submit: response is not JSON. First 200 chars:', rawText.slice(0, 200));
        throw parseErr;
      }

      if (!r.ok) {
        const errorMessage = data.description || data.message || 'Unable to add item to cart';
        atcLog('Form submit: API error', errorMessage, data);
        showErrorBanner(errorMessage);
        return;
      }
      if (data.blocked) atcLog('Form submit: response had data.blocked=', data.blocked);

      addToCartSucceeded = true;
      blockCartRedirect(1500);
      clearAtcOpenDrawerFallback();
      openDrawer();

      try {
        await fetchAndRender();
      } catch (renderErr) {
        console.warn('fetchAndRender error (non-critical):', renderErr);
      }

      try {
        document.dispatchEvent(new CustomEvent('cart:added'));
      } catch (eventErr) {
        console.warn('cart:added event error (non-critical):', eventErr);
      }
    } catch (e) {
      atcLog('[ATC-ERROR-1] Form submit handler error:', e?.message || e, e);
      console.warn('[ATC-ERROR-1] Form submit handler error', e);
      if (!addToCartSucceeded) {
        showErrorBanner('An error occurred while adding to cart');
      }
    } finally {
      setTimeout(() => {
        isProcessingATC = false;
      }, 500);
    }
  }, true);

  // CLICK LISTENER
  document.addEventListener('click', async (ev) => {
    // NEXUS CRO hero owns ATC (CCS addon + Extend) — do not FormData-post the form alone.
    if (
      window.__nexusCroAtcHandled ||
      ev.target.closest('nexus-cro-hero, [data-nc-hero-atc], .nc-hero__atc')
    ) {
      console.log('[CART-DRAWER] NEXUS CRO hero ATC — skipping');
      return;
    }

    // Vortex hero owns ATC when the Extend offer is on the page.
    if (
      window.__vpAtcHandled ||
      (document.querySelector('[data-vp-extend-offer]') && ev.target.closest('[data-vp-hero], [data-vp-sticky]'))
    ) {
      console.log('[CART-DRAWER] Vortex hero ATC — skipping');
      return;
    }

    const btn = ev.target.closest(SEL.addButtons);
    if (!btn) return;

    // Check if this click is from sticky bar button
    const isFromStickyBar = btn.closest('.sticky-bar, .sticky-bar-form');
    if (isFromStickyBar) {
      console.log('[CART-DRAWER] Click from sticky bar detected, letting addtocart-sticky.js handle it');
      return; // Let addtocart-sticky.js handle it
    }

    // Check if addtocart-sticky.js already handled this
    if (window.__stickyAtcHandled) {
      console.log('[CART-DRAWER] Sticky already handled flag detected');
      return;
    }

    console.log('[CART-DRAWER] CLICK handler processing');

    if (isProcessingATC) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    const form = btn.closest('form[action*="/cart/add"]');
    const dataVariantId = btn.getAttribute('data-variant-id');
    atcLog('Click: form=', !!form, 'data-variant-id=', dataVariantId, 'btn classes=', btn.className);
    const extendInThisForm = !!(form && form.querySelector('.extend-offer'));
    const sellingPlanInput = form ? form.querySelector('input[name="selling_plan"]') : null;
    const subsTypeRadio = document.querySelector('input[name^="subs_type_"]:checked');
    const isSubscriptionSelected = subsTypeRadio && subsTypeRadio.getAttribute('data-type') === 'subscription';

    let sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';
    if (isSubscriptionSelected && !sellingPlanValue) {
      const intervalSelect = document.querySelector('select[name="subs_interval"]');
      if (intervalSelect && sellingPlanInput) {
        sellingPlanValue = intervalSelect.value;
        sellingPlanInput.value = sellingPlanValue;
      }
    }

    const hasSubscription = isSubscriptionSelected && sellingPlanValue && sellingPlanValue !== '';

    if (form && extendInThisForm && ev.isTrusted && !hasSubscription) {
      return;
    }

    if (form && extendInThisForm && hasSubscription) {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      window.__blockingExtendForSubscription = true;
      setTimeout(() => delete window.__blockingExtendForSubscription, 2000);
    }

    if (form) {
      atcLog('PATH: BUTTON WITH FORM');
      if (!ev.defaultPrevented) ev.preventDefault();
      if (ev.stopPropagation) ev.stopPropagation();
      if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
      isProcessingATC = true;
      scheduleAtcOpenDrawerFallback();

      let addToCartSucceeded = false;
      try {
        const fd = new FormData(form);
        atcLog('Button+form: posting to /cart/add.js');
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd,
          credentials: 'same-origin'
        });

        const rawText = await r.text();
        atcLog('Button+form response: status=', r.status, 'body length=', rawText.length);
        let data;
        try {
          data = JSON.parse(rawText);
        } catch (parseErr) {
          atcLog('Button+form: response is not JSON. First 200 chars:', rawText.slice(0, 200));
          throw parseErr;
        }

        if (!r.ok) {
          const errorMessage = data.description || data.message || 'Unable to add item to cart';
          atcLog('Button+form: API error', errorMessage, data);
          showErrorBanner(errorMessage);
          return;
        }
        if (data.blocked) atcLog('Button+form: response had data.blocked=', data.blocked);

        addToCartSucceeded = true;
        blockCartRedirect(1500);
        clearAtcOpenDrawerFallback();
        openDrawer();

        try {
          await fetchAndRender();
        } catch (renderErr) {
          console.warn('fetchAndRender error (non-critical):', renderErr);
        }

        try {
          document.dispatchEvent(new CustomEvent('cart:added'));
        } catch (eventErr) {
          console.warn('cart:added event error (non-critical):', eventErr);
        }
      } catch (e) {
        atcLog('[ATC-ERROR-2] Button with form error:', e?.message || e, e);
        console.warn('[ATC-ERROR-2] Button with form handler error', e);
        if (!addToCartSucceeded) {
          showErrorBanner('An error occurred while adding to cart');
        }
      } finally {
        setTimeout(() => {
          isProcessingATC = false;
        }, 500);
      }
      return;
    }

    atcLog('PATH: NO FORM (collection/quick-add). Building FormData from button.');
    ev.preventDefault();
    ev.stopPropagation();
    isProcessingATC = true;
    scheduleAtcOpenDrawerFallback();

    try {
      const fd = buildFormDataFromButton(btn);
      const variantIdFromBtn = fd.get('id');
      atcLog('No-form: posting variant id=', variantIdFromBtn, 'qty=', fd.get('quantity'));
      const r = await fetch('/cart/add.js', {
        method:'POST',
        headers:{'Accept':'application/json'},
        body: fd,
        credentials:'same-origin'
      });

      const rawText = await r.text();
      atcLog('No-form response: status=', r.status, 'body length=', rawText.length);
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        atcLog('No-form: response is not JSON. First 200 chars:', rawText.slice(0, 200));
        throw parseErr;
      }

      if (!r.ok) {
        const errorMessage = data.description || data.message || 'Unable to add item to cart';
        atcLog('No-form: API error', errorMessage, data);
        showErrorBanner(errorMessage);
        return;
      }
      if (data.blocked) atcLog('No-form: response had data.blocked=', data.blocked);

      blockCartRedirect(1500);
      clearAtcOpenDrawerFallback();
      openDrawer();

      try {
        await fetchAndRender();
      } catch (renderErr) {
        console.warn('fetchAndRender error (non-critical):', renderErr);
      }

      try {
        document.dispatchEvent(new CustomEvent('cart:added'));
      } catch (eventErr) {
        console.warn('cart:added event error (non-critical):', eventErr);
      }
      return;
    } catch (primaryErr) {
      atcLog('No-form primary path failed:', primaryErr?.message || primaryErr, primaryErr);
      let fallbackAddSucceeded = false;
      try {
        // Support both product page (.product-summary) and collection grid ([data-product-item])
        const card = btn.closest('.product-summary, [data-product-item], .productgrid--item');
        let handle = card?.dataset?.handle;
        if (!handle && card) {
          const quickshopUrl = card.getAttribute('data-product-quickshop-url');
          if (quickshopUrl) {
            const match = quickshopUrl.match(/\/products\/([^/?]+)/);
            if (match) handle = match[1];
          }
          if (!handle) {
            const compareDataEl = card.querySelector('[data-product-compare-data]');
            if (compareDataEl) {
              try {
                const data = JSON.parse(compareDataEl.textContent);
                if (data.handle) handle = data.handle;
              } catch (_) {}
            }
          }
        }
        atcLog('Fallback: card=', !!card, 'handle=', handle);
        if (!handle) throw new Error('No variant id/form/handle');

        atcLog('Fallback: fetching variant id for handle', handle);
        const variantId = await getFirstAvailableVariantIdByHandle(handle);
        atcLog('Fallback: got variantId=', variantId, 'calling addVariantToCart');
        await addVariantToCart(variantId, 1);
        fallbackAddSucceeded = true;

        blockCartRedirect(1500);
        clearAtcOpenDrawerFallback();
        openDrawer();

        try {
          await fetchAndRender();
        } catch (renderErr) {
          console.warn('fetchAndRender error (non-critical):', renderErr);
        }

        try {
          document.dispatchEvent(new CustomEvent('cart:added'));
        } catch (eventErr) {
          console.warn('cart:added event error (non-critical):', eventErr);
        }
        return;
      } catch(e){
        atcLog('[ATC-ERROR-3] Fallback failed:', e?.message || e, e);
        console.warn('[ATC-ERROR-3] Fallback handler error', e);
        // Only show error if addVariantToCart actually failed
        if (!fallbackAddSucceeded) {
          showErrorBanner('An error occurred while adding to cart');
        }
      }
    } finally {
      setTimeout(() => {
        isProcessingATC = false;
      }, 500);
    }
  }, true);

  function buildFormDataFromButton(btn) {
    const id = btn.getAttribute('data-variant-id');
    if (id) {
      const fd = new FormData();
      fd.append('id', id);
      const qty = btn.closest('.productitem, .product, form')
        ?.querySelector('input[name="quantity"], input[data-quantity], .quantity__input')
        ?.value || '1';
      fd.append('quantity', qty);
      return fd;
    }
    const form = btn.closest('form[action*="/cart/add"]') || btn.closest('form');
    if (form) return new FormData(form);
    throw new Error('No variant id/form for ATC');
  }

  // CART DRAWER INTERACTIONS
  document.addEventListener('click', (e) => {
    if (!isOpen()) return;

    const closeBtn = e.target.closest(SEL.close);
    if (closeBtn) { e.preventDefault(); closeDrawer(); return; }

    syncEls();
    if (els.overlay && (e.target === els.overlay || els.overlay.contains(e.target))) {
      e.preventDefault(); closeDrawer(); return;
    }

    const plus = e.target.closest(SEL.qtyPlus);
    if (plus) {
      const row = plus.closest(SEL.lineWrap);
      const input = row?.querySelector(SEL.qtyInput);
      updateLine(row?.dataset.line || plus.getAttribute('data-line'), Number(input?.value || 0) + 1);
      return;
    }

    const minus = e.target.closest(SEL.qtyMinus);
    if (minus) {
      const row = minus.closest(SEL.lineWrap);
      const input = row?.querySelector(SEL.qtyInput);
      updateLine(row?.dataset.line || minus.getAttribute('data-line'), Math.max(0, Number(input?.value || 0) - 1));
      return;
    }

    const rm = e.target.closest(SEL.remove);
    if (rm) {
      const row = rm.closest(SEL.lineWrap);
      if (row) {
        setTimeout(() => {
          row.style.opacity = '0';
          row.style.maxHeight = '0';
          row.style.margin = '0';
          row.style.paddingTop = '0';
          row.style.paddingBottom = '0';
          row.style.overflow = 'hidden';
          row.style.pointerEvents = 'none';
        }, 1000);
      }
      updateLine(row?.dataset.line || rm.getAttribute('data-line'), 0);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!isOpen()) return;

    if (e.key === 'Escape') {
      e.preventDefault(); closeDrawer(); return;
    }

    if (e.key === 'Tab') {
      syncEls();
      if (!els.panel) return;

      if (!isInPanel(document.activeElement)) {
        e.preventDefault();
        computeFocusables();
        (firstFocusable || els.panel).focus({ preventScroll: true });
        return;
      }

      computeFocusables();
      if (focusables.length === 0) {
        e.preventDefault(); els.panel.focus(); return;
      }
      const backwards = e.shiftKey;
      if (!backwards && document.activeElement === lastFocusable) {
        e.preventDefault(); firstFocusable.focus();
      } else if (backwards && document.activeElement === firstFocusable) {
        e.preventDefault(); lastFocusable.focus();
      }
    }
  }, true);

  document.addEventListener('click', (e) => {
    const trg = e.target.closest('[data-open-cart]');
    if (!trg) return;
    e.preventDefault();
    openDrawer();
    fetchAndRender().catch(()=>{});
  });

  document.addEventListener('click', (e) => {
    const checkoutBtn = e.target.closest('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout');
    if (!checkoutBtn) return;
    
    if (checkoutBtn.disabled || checkoutBtn.hasAttribute('disabled')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    window.location.href = '/checkout';
  }, true);

  const mo = new MutationObserver(() => { if (isOpen()) computeFocusables(); });
  mo.observe(document.documentElement, { subtree:true, childList:true, attributes:true });

})();

// STICKY ATC BAR HANDLER
(function () {
  const STICKY_ATC_SELECTOR = '.sticky-bar .product-form--atc-button, .sticky-bar [data-product-atc]';
  const PDP_ATC_SELECTOR    = 'button[data-product-atc], .product-form--atc-button';
  const POLL_INTERVAL_MS    = 100;
  const POLL_TIMEOUT_MS     = 2000;
  let isProcessingATC = false;

  async function waitForExtendOffer(timeout = POLL_TIMEOUT_MS) {
    const start = Date.now();
    return new Promise((resolve) => {
      const check = () => {
        const extendOffer = window.__ExtendPdp?.extendOffer || document.querySelector('.extend-offer');
        if (extendOffer) return resolve(extendOffer);
        if (Date.now() - start > timeout) return resolve(null);
        setTimeout(check, POLL_INTERVAL_MS);
      };
      check();
    });
  }

  function getVariantIdAndQtyFromSticky(btn) {
    const form = btn.closest('form[action*="/cart/add"], form');
    let variantId = null;
    let qty = 1;
    try {
      if (form) {
        const idEl = form.querySelector('input[name="id"], select[name="id"]');
        if (idEl) variantId = idEl.value;
        const qEl = form.querySelector('input[name="quantity"], [data-quantity-input]');
        if (qEl) qty = Math.max(1, parseInt(qEl.value || qEl.getAttribute('value') || '1', 10));
      }
    } catch (_) {}
    try {
      if ((!variantId || variantId === 'null' || variantId === 'undefined') && window.__ExtendPdp?.productForm) {
        const pdpIdEl = window.__ExtendPdp.productForm.querySelector('input[name="id"], select[name="id"]');
        if (pdpIdEl) variantId = pdpIdEl.value;
      }
    } catch (_) {}
    return { variantId: variantId ? String(variantId) : null, qty };
  }

  async function callExtendHandleAddToCart(extendOffer, variantId, qty) {
    try {
      window.__forwardingExtendClick = true;

      try {
        if (variantId && typeof Extend?.setActiveProduct === 'function') {
          Extend.setActiveProduct(extendOffer, { referenceId: String(variantId), price: undefined, category: undefined });
        } else if (variantId && Extend && Extend.integration) {
          Extend.integration.offerProperties = { variant: String(variantId), category: undefined, price: undefined };
        }
      } catch (err) {
        console.warn('Sticky: Extend.setActiveProduct failed', err);
      }

      if (typeof ExtendShopify?.handleAddToCart === 'function') {
        ExtendShopify.handleAddToCart(extendOffer, {
          quantity: qty || 1,
          modal: true,
          done: function () {
            try { delete window.__forwardingExtendClick; } catch(_) {}

            try {
              if (typeof window.openDrawer === 'function') window.openDrawer();
              if (typeof window.fetchAndRender === 'function') window.fetchAndRender().catch(()=>{});
              window.setTimeout(() => window.dispatchEvent(new Event('refreshAjaxSideCart')), 150);
            } catch (err) {
              console.warn('Sticky: error opening side cart after Extend done', err);
            }
          }
        });
        return true;
      } else {
        setTimeout(() => { try { delete window.__forwardingExtendClick; } catch(_){} }, 1200);
        return false;
      }
    } catch (err) {
      try { delete window.__forwardingExtendClick; } catch(_) {}
      console.warn('Sticky: callExtendHandleAddToCart error', err);
      return false;
    }
  }

  document.addEventListener('click', async (e) => {
  const stickyBtn = e.target.closest(STICKY_ATC_SELECTOR);
  if (!stickyBtn) return;

  // Check if this is a sticky bar button (not PDP button)
  // If so, let addtocart-sticky.js handle it instead
  const isActualStickyBar = stickyBtn.closest('.sticky-bar, .sticky-bar-form');
  if (isActualStickyBar) {
    // This is handled by addtocart-sticky.js, don't process here
    return;
  }

  // Check if addtocart-sticky.js already handled this
  if (window.__stickyAtcHandled) {
    return;
  }

  if (isProcessingATC) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  const formId = stickyBtn.getAttribute('form');
  const mainForm = formId ? document.getElementById(formId) : null;
  const sellingPlanInput = mainForm ? mainForm.querySelector('input[name="selling_plan"]') : null;
  const subsTypeRadio = document.querySelector('input[name^="subs_type_"]:checked');
  const isSubscriptionSelected = subsTypeRadio && subsTypeRadio.value !== 'one_time';

  let sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';
  if (isSubscriptionSelected && !sellingPlanValue) {
    const intervalSelect = document.querySelector('select[name="subs_interval"]');
    if (intervalSelect && sellingPlanInput) {
      sellingPlanValue = intervalSelect.value;
      sellingPlanInput.value = sellingPlanValue;
    }
  }

  const hasSubscription = isSubscriptionSelected && sellingPlanValue && sellingPlanValue !== '';

  if (hasSubscription) {
    return;
  }

  if (!(window.Extend && window.ExtendShopify)) {
    return;
  }

  if (e.preventDefault) e.preventDefault();
  if (e.stopPropagation) e.stopPropagation();

  const { variantId, qty } = getVariantIdAndQtyFromSticky(stickyBtn);

  const extendOffer = await waitForExtendOffer();

  if (extendOffer) {
    window.__forwardingExtendClick = true;
    const ok = await callExtendHandleAddToCart(extendOffer, variantId, qty);
    if (ok) {
      setTimeout(() => { try { delete window.__forwardingExtendClick; } catch(_){} }, 1200);
      return;
    }
  }

  const pdpAtc = document.querySelector(PDP_ATC_SELECTOR);
  if (pdpAtc) {
    window.__forwardingExtendClick = true;
    try {
      pdpAtc.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    } catch (_) {
      try { pdpAtc.click(); } catch(_) {}
    }
    setTimeout(() => { try { delete window.__forwardingExtendClick; } catch(_){} }, 1200);
    return;
  }

  console.warn('Sticky: could not trigger Extend modal and PDP ATC not found; falling back to normal sticky behavior');
}, true);
})();
      }
    } finally {
      setTimeout(() => {
        isProcessingATC = false;
      }, 500);
    }
  }, true);

  // CLICK LISTENER
  document.addEventListener('click', async (ev) => {
    // NEXUS CRO hero owns ATC (CCS addon + Extend) — do not FormData-post the form alone.
    if (
      window.__nexusCroAtcHandled ||
      ev.target.closest('nexus-cro-hero, [data-nc-hero-atc], .nc-hero__atc')
    ) {
      console.log('[CART-DRAWER] NEXUS CRO hero ATC — skipping');
      return;
    }

    // Vortex hero owns ATC when the Extend offer is on the page.
    if (
      window.__vpAtcHandled ||
      (document.querySelector('[data-vp-extend-offer]') && ev.target.closest('[data-vp-hero], [data-vp-sticky]'))
    ) {
      console.log('[CART-DRAWER] Vortex hero ATC — skipping');
      return;
    }

    const btn = ev.target.closest(SEL.addButtons);
    if (!btn) return;

    // Check if this click is from sticky bar button
    const isFromStickyBar = btn.closest('.sticky-bar, .sticky-bar-form');
    if (isFromStickyBar) {
      console.log('[CART-DRAWER] Click from sticky bar detected, letting addtocart-sticky.js handle it');
      return; // Let addtocart-sticky.js handle it
    }

    // Check if addtocart-sticky.js already handled this
    if (window.__stickyAtcHandled) {
      console.log('[CART-DRAWER] Sticky already handled flag detected');
      return;
    }

    console.log('[CART-DRAWER] CLICK handler processing');

    if (isProcessingATC) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    const form = btn.closest('form[action*="/cart/add"]');
    const dataVariantId = btn.getAttribute('data-variant-id');
    atcLog('Click: form=', !!form, 'data-variant-id=', dataVariantId, 'btn classes=', btn.className);
    const extendInThisForm = !!(form && form.querySelector('.extend-offer'));
    const sellingPlanInput = form ? form.querySelector('input[name="selling_plan"]') : null;
    const subsTypeRadio = document.querySelector('input[name^="subs_type_"]:checked');
    const isSubscriptionSelected = subsTypeRadio && subsTypeRadio.getAttribute('data-type') === 'subscription';

    let sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';
    if (isSubscriptionSelected && !sellingPlanValue) {
      const intervalSelect = document.querySelector('select[name="subs_interval"]');
      if (intervalSelect && sellingPlanInput) {
        sellingPlanValue = intervalSelect.value;
        sellingPlanInput.value = sellingPlanValue;
      }
    }

    const hasSubscription = isSubscriptionSelected && sellingPlanValue && sellingPlanValue !== '';

    if (form && extendInThisForm && ev.isTrusted && !hasSubscription) {
      return;
    }

    if (form && extendInThisForm && hasSubscription) {
      ev.preventDefault();
      ev.stopPropagation();
      ev.stopImmediatePropagation();
      window.__blockingExtendForSubscription = true;
      setTimeout(() => delete window.__blockingExtendForSubscription, 2000);
    }

    if (form) {
      atcLog('PATH: BUTTON WITH FORM');
      if (!ev.defaultPrevented) ev.preventDefault();
      if (ev.stopPropagation) ev.stopPropagation();
      if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
      isProcessingATC = true;
      scheduleAtcOpenDrawerFallback();

      let addToCartSucceeded = false;
      try {
        const fd = new FormData(form);
        atcLog('Button+form: posting to /cart/add.js');
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd,
          credentials: 'same-origin'
        });

        const rawText = await r.text();
        atcLog('Button+form response: status=', r.status, 'body length=', rawText.length);
        let data;
        try {
          data = JSON.parse(rawText);
        } catch (parseErr) {
          atcLog('Button+form: response is not JSON. First 200 chars:', rawText.slice(0, 200));
          throw parseErr;
        }

        if (!r.ok) {
          const errorMessage = data.description || data.message || 'Unable to add item to cart';
          atcLog('Button+form: API error', errorMessage, data);
          showErrorBanner(errorMessage);
          return;
        }
        if (data.blocked) atcLog('Button+form: response had data.blocked=', data.blocked);

        addToCartSucceeded = true;
        blockCartRedirect(1500);
        clearAtcOpenDrawerFallback();
        openDrawer();

        try {
          await fetchAndRender();
        } catch (renderErr) {
          console.warn('fetchAndRender error (non-critical):', renderErr);
        }

        try {
          document.dispatchEvent(new CustomEvent('cart:added'));
        } catch (eventErr) {
          console.warn('cart:added event error (non-critical):', eventErr);
        }
      } catch (e) {
        atcLog('[ATC-ERROR-2] Button with form error:', e?.message || e, e);
        console.warn('[ATC-ERROR-2] Button with form handler error', e);
        if (!addToCartSucceeded) {
          showErrorBanner('An error occurred while adding to cart');
        }
      } finally {
        setTimeout(() => {
          isProcessingATC = false;
        }, 500);
      }
      return;
    }

    atcLog('PATH: NO FORM (collection/quick-add). Building FormData from button.');
    ev.preventDefault();
    ev.stopPropagation();
    isProcessingATC = true;
    scheduleAtcOpenDrawerFallback();

    try {
      const fd = buildFormDataFromButton(btn);
      const variantIdFromBtn = fd.get('id');
      atcLog('No-form: posting variant id=', variantIdFromBtn, 'qty=', fd.get('quantity'));
      const r = await fetch('/cart/add.js', {
        method:'POST',
        headers:{'Accept':'application/json'},
        body: fd,
        credentials:'same-origin'
      });

      const rawText = await r.text();
      atcLog('No-form response: status=', r.status, 'body length=', rawText.length);
      let data;
      try {
        data = JSON.parse(rawText);
      } catch (parseErr) {
        atcLog('No-form: response is not JSON. First 200 chars:', rawText.slice(0, 200));
        throw parseErr;
      }

      if (!r.ok) {
        const errorMessage = data.description || data.message || 'Unable to add item to cart';
        atcLog('No-form: API error', errorMessage, data);
        showErrorBanner(errorMessage);
        return;
      }
      if (data.blocked) atcLog('No-form: response had data.blocked=', data.blocked);

      blockCartRedirect(1500);
      clearAtcOpenDrawerFallback();
      openDrawer();

      try {
        await fetchAndRender();
      } catch (renderErr) {
        console.warn('fetchAndRender error (non-critical):', renderErr);
      }

      try {
        document.dispatchEvent(new CustomEvent('cart:added'));
      } catch (eventErr) {
        console.warn('cart:added event error (non-critical):', eventErr);
      }
      return;
    } catch (primaryErr) {
      atcLog('No-form primary path failed:', primaryErr?.message || primaryErr, primaryErr);
      let fallbackAddSucceeded = false;
      try {
        // Support both product page (.product-summary) and collection grid ([data-product-item])
        const card = btn.closest('.product-summary, [data-product-item], .productgrid--item');
        let handle = card?.dataset?.handle;
        if (!handle && card) {
          const quickshopUrl = card.getAttribute('data-product-quickshop-url');
          if (quickshopUrl) {
            const match = quickshopUrl.match(/\/products\/([^/?]+)/);
            if (match) handle = match[1];
          }
          if (!handle) {
            const compareDataEl = card.querySelector('[data-product-compare-data]');
            if (compareDataEl) {
              try {
                const data = JSON.parse(compareDataEl.textContent);
                if (data.handle) handle = data.handle;
              } catch (_) {}
            }
          }
        }
        atcLog('Fallback: card=', !!card, 'handle=', handle);
        if (!handle) throw new Error('No variant id/form/handle');

        atcLog('Fallback: fetching variant id for handle', handle);
        const variantId = await getFirstAvailableVariantIdByHandle(handle);
        atcLog('Fallback: got variantId=', variantId, 'calling addVariantToCart');
        await addVariantToCart(variantId, 1);
        fallbackAddSucceeded = true;

        blockCartRedirect(1500);
        clearAtcOpenDrawerFallback();
        openDrawer();

        try {
          await fetchAndRender();
        } catch (renderErr) {
          console.warn('fetchAndRender error (non-critical):', renderErr);
        }

        try {
          document.dispatchEvent(new CustomEvent('cart:added'));
        } catch (eventErr) {
          console.warn('cart:added event error (non-critical):', eventErr);
        }
        return;
      } catch(e){
        atcLog('[ATC-ERROR-3] Fallback failed:', e?.message || e, e);
        console.warn('[ATC-ERROR-3] Fallback handler error', e);
        // Only show error if addVariantToCart actually failed
        if (!fallbackAddSucceeded) {
          showErrorBanner('An error occurred while adding to cart');
        }
      }
    } finally {
      setTimeout(() => {
        isProcessingATC = false;
      }, 500);
    }
  }, true);

  function buildFormDataFromButton(btn) {
    const id = btn.getAttribute('data-variant-id');
    if (id) {
      const fd = new FormData();
      fd.append('id', id);
      const qty = btn.closest('.productitem, .product, form')
        ?.querySelector('input[name="quantity"], input[data-quantity], .quantity__input')
        ?.value || '1';
      fd.append('quantity', qty);
      return fd;
    }
    const form = btn.closest('form[action*="/cart/add"]') || btn.closest('form');
    if (form) return new FormData(form);
    throw new Error('No variant id/form for ATC');
  }

  // CART DRAWER INTERACTIONS
  document.addEventListener('click', (e) => {
    if (!isOpen()) return;

    const closeBtn = e.target.closest(SEL.close);
    if (closeBtn) { e.preventDefault(); closeDrawer(); return; }

    syncEls();
    if (els.overlay && (e.target === els.overlay || els.overlay.contains(e.target))) {
      e.preventDefault(); closeDrawer(); return;
    }

    const plus = e.target.closest(SEL.qtyPlus);
    if (plus) {
      const row = plus.closest(SEL.lineWrap);
      const input = row?.querySelector(SEL.qtyInput);
      updateLine(row?.dataset.line || plus.getAttribute('data-line'), Number(input?.value || 0) + 1);
      return;
    }

    const minus = e.target.closest(SEL.qtyMinus);
    if (minus) {
      const row = minus.closest(SEL.lineWrap);
      const input = row?.querySelector(SEL.qtyInput);
      updateLine(row?.dataset.line || minus.getAttribute('data-line'), Math.max(0, Number(input?.value || 0) - 1));
      return;
    }

    const rm = e.target.closest(SEL.remove);
    if (rm) {
      const row = rm.closest(SEL.lineWrap);
      if (row) {
        setTimeout(() => {
          row.style.opacity = '0';
          row.style.maxHeight = '0';
          row.style.margin = '0';
          row.style.paddingTop = '0';
          row.style.paddingBottom = '0';
          row.style.overflow = 'hidden';
          row.style.pointerEvents = 'none';
        }, 1000);
      }
      updateLine(row?.dataset.line || rm.getAttribute('data-line'), 0);
      return;
    }
  });

  document.addEventListener('keydown', (e) => {
    if (!isOpen()) return;

    if (e.key === 'Escape') {
      e.preventDefault(); closeDrawer(); return;
    }

    if (e.key === 'Tab') {
      syncEls();
      if (!els.panel) return;

      if (!isInPanel(document.activeElement)) {
        e.preventDefault();
        computeFocusables();
        (firstFocusable || els.panel).focus({ preventScroll: true });
        return;
      }

      computeFocusables();
      if (focusables.length === 0) {
        e.preventDefault(); els.panel.focus(); return;
      }
      const backwards = e.shiftKey;
      if (!backwards && document.activeElement === lastFocusable) {
        e.preventDefault(); firstFocusable.focus();
      } else if (backwards && document.activeElement === firstFocusable) {
        e.preventDefault(); lastFocusable.focus();
      }
    }
  }, true);

  document.addEventListener('click', (e) => {
    const trg = e.target.closest('[data-open-cart]');
    if (!trg) return;
    e.preventDefault();
    openDrawer();
    fetchAndRender().catch(()=>{});
  });

  document.addEventListener('click', (e) => {
    const checkoutBtn = e.target.closest('[name="checkout"], button[data-checkout], .checkout-button, .atc-button--checkout');
    if (!checkoutBtn) return;
    
    if (checkoutBtn.disabled || checkoutBtn.hasAttribute('disabled')) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    e.preventDefault();
    e.stopPropagation();
    window.location.href = '/checkout';
  }, true);

  const mo = new MutationObserver(() => { if (isOpen()) computeFocusables(); });
  mo.observe(document.documentElement, { subtree:true, childList:true, attributes:true });

})();

// STICKY ATC BAR HANDLER
(function () {
  const STICKY_ATC_SELECTOR = '.sticky-bar .product-form--atc-button, .sticky-bar [data-product-atc]';
  const PDP_ATC_SELECTOR    = 'button[data-product-atc], .product-form--atc-button';
  const POLL_INTERVAL_MS    = 100;
  const POLL_TIMEOUT_MS     = 2000;
  let isProcessingATC = false;

  async function waitForExtendOffer(timeout = POLL_TIMEOUT_MS) {
    const start = Date.now();
    return new Promise((resolve) => {
      const check = () => {
        const extendOffer = window.__ExtendPdp?.extendOffer || document.querySelector('.extend-offer');
        if (extendOffer) return resolve(extendOffer);
        if (Date.now() - start > timeout) return resolve(null);
        setTimeout(check, POLL_INTERVAL_MS);
      };
      check();
    });
  }

  function getVariantIdAndQtyFromSticky(btn) {
    const form = btn.closest('form[action*="/cart/add"], form');
    let variantId = null;
    let qty = 1;
    try {
      if (form) {
        const idEl = form.querySelector('input[name="id"], select[name="id"]');
        if (idEl) variantId = idEl.value;
        const qEl = form.querySelector('input[name="quantity"], [data-quantity-input]');
        if (qEl) qty = Math.max(1, parseInt(qEl.value || qEl.getAttribute('value') || '1', 10));
      }
    } catch (_) {}
    try {
      if ((!variantId || variantId === 'null' || variantId === 'undefined') && window.__ExtendPdp?.productForm) {
        const pdpIdEl = window.__ExtendPdp.productForm.querySelector('input[name="id"], select[name="id"]');
        if (pdpIdEl) variantId = pdpIdEl.value;
      }
    } catch (_) {}
    return { variantId: variantId ? String(variantId) : null, qty };
  }

  async function callExtendHandleAddToCart(extendOffer, variantId, qty) {
    try {
      window.__forwardingExtendClick = true;

      try {
        if (variantId && typeof Extend?.setActiveProduct === 'function') {
          Extend.setActiveProduct(extendOffer, { referenceId: String(variantId), price: undefined, category: undefined });
        } else if (variantId && Extend && Extend.integration) {
          Extend.integration.offerProperties = { variant: String(variantId), category: undefined, price: undefined };
        }
      } catch (err) {
        console.warn('Sticky: Extend.setActiveProduct failed', err);
      }

      if (typeof ExtendShopify?.handleAddToCart === 'function') {
        ExtendShopify.handleAddToCart(extendOffer, {
          quantity: qty || 1,
          modal: true,
          done: function () {
            try { delete window.__forwardingExtendClick; } catch(_) {}

            try {
              if (typeof window.openDrawer === 'function') window.openDrawer();
              if (typeof window.fetchAndRender === 'function') window.fetchAndRender().catch(()=>{});
              window.setTimeout(() => window.dispatchEvent(new Event('refreshAjaxSideCart')), 150);
            } catch (err) {
              console.warn('Sticky: error opening side cart after Extend done', err);
            }
          }
        });
        return true;
      } else {
        setTimeout(() => { try { delete window.__forwardingExtendClick; } catch(_){} }, 1200);
        return false;
      }
    } catch (err) {
      try { delete window.__forwardingExtendClick; } catch(_) {}
      console.warn('Sticky: callExtendHandleAddToCart error', err);
      return false;
    }
  }

  document.addEventListener('click', async (e) => {
  const stickyBtn = e.target.closest(STICKY_ATC_SELECTOR);
  if (!stickyBtn) return;

  // Check if this is a sticky bar button (not PDP button)
  // If so, let addtocart-sticky.js handle it instead
  const isActualStickyBar = stickyBtn.closest('.sticky-bar, .sticky-bar-form');
  if (isActualStickyBar) {
    // This is handled by addtocart-sticky.js, don't process here
    return;
  }

  // Check if addtocart-sticky.js already handled this
  if (window.__stickyAtcHandled) {
    return;
  }

  if (isProcessingATC) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  const formId = stickyBtn.getAttribute('form');
  const mainForm = formId ? document.getElementById(formId) : null;
  const sellingPlanInput = mainForm ? mainForm.querySelector('input[name="selling_plan"]') : null;
  const subsTypeRadio = document.querySelector('input[name^="subs_type_"]:checked');
  const isSubscriptionSelected = subsTypeRadio && subsTypeRadio.value !== 'one_time';

  let sellingPlanValue = sellingPlanInput ? sellingPlanInput.value : '';
  if (isSubscriptionSelected && !sellingPlanValue) {
    const intervalSelect = document.querySelector('select[name="subs_interval"]');
    if (intervalSelect && sellingPlanInput) {
      sellingPlanValue = intervalSelect.value;
      sellingPlanInput.value = sellingPlanValue;
    }
  }

  const hasSubscription = isSubscriptionSelected && sellingPlanValue && sellingPlanValue !== '';

  if (hasSubscription) {
    return;
  }

  if (!(window.Extend && window.ExtendShopify)) {
    return;
  }

  if (e.preventDefault) e.preventDefault();
  if (e.stopPropagation) e.stopPropagation();

  const { variantId, qty } = getVariantIdAndQtyFromSticky(stickyBtn);

  const extendOffer = await waitForExtendOffer();

  if (extendOffer) {
    window.__forwardingExtendClick = true;
    const ok = await callExtendHandleAddToCart(extendOffer, variantId, qty);
    if (ok) {
      setTimeout(() => { try { delete window.__forwardingExtendClick; } catch(_){} }, 1200);
      return;
    }
  }

  const pdpAtc = document.querySelector(PDP_ATC_SELECTOR);
  if (pdpAtc) {
    window.__forwardingExtendClick = true;
    try {
      pdpAtc.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    } catch (_) {
      try { pdpAtc.click(); } catch(_) {}
    }
    setTimeout(() => { try { delete window.__forwardingExtendClick; } catch(_){} }, 1200);
    return;
  }

  console.warn('Sticky: could not trigger Extend modal and PDP ATC not found; falling back to normal sticky behavior');
}, true);
})();